import { AdminSectionCard } from '../primitives/admin-section-card.js';
import { AdminEmptyState } from '../primitives/admin-empty-state.js';
import { AdminErrorState } from '../primitives/admin-error-state.js';
import { AdminSkeleton } from '../primitives/admin-skeleton.js';
import { AdminStatusBadge } from '../primitives/admin-status-badge.js';
import { formatCount } from '../../lib/format.js';
import type { LowStockItemDto } from '../../types/dashboard.js';

export interface LowStockAlertsProps {
  items: LowStockItemDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** Bounded (server caps at 10), no inventory-mutation actions here -- read-only alert list, warehouse internals never exposed (Part 10). */
export function LowStockAlerts({ items, isLoading, isError, onRetry }: LowStockAlertsProps) {
  return (
    <AdminSectionCard title="Low-stock alerts" description="Active variants at or below their low-stock threshold">
      {isLoading && <AdminSkeleton lines={4} className="h-10" />}
      {!isLoading && isError && <AdminErrorState message="Inventory alerts could not be loaded." onRetry={onRetry} />}
      {!isLoading && !isError && items.length === 0 && (
        <AdminEmptyState title="Nothing low on stock" description="Every active variant is above its low-stock threshold." />
      )}
      {!isLoading && !isError && items.length > 0 && (
        <ul className="divide-y divide-gray-50">
          {items.map((item) => (
            <li key={item.variantId} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {item.productName} <span className="font-normal text-gray-500">— {item.variantName}</span>
                </p>
                <p className="text-xs text-gray-400">SKU {item.sku}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm text-gray-600">
                  {formatCount(item.quantityAvailable)} / {formatCount(item.lowStockThreshold)}
                </span>
                <AdminStatusBadge
                  label={item.status === 'out_of_stock' ? 'Out of stock' : 'Low stock'}
                  tone={item.status === 'out_of_stock' ? 'danger' : 'warning'}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </AdminSectionCard>
  );
}
